import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { AlertCircle, Calendar, Check, ChevronDown, ChevronUp, Loader2, MapPin, Users } from "lucide-react";
import { useCallback, useEffect, useRef, useState, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "@/components/Footer";
import { BackButton } from "@/components/ui/back-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

// 타입 가드 함수
interface PostgresError {
  code?: string;
  message?: string;
}

const isPostgresError = (error: unknown): error is PostgresError => {
  return typeof error === 'object' && error !== null && 
         ('code' in error || 'message' in error);
};
import { useBookingData } from "@/hooks/useBookingData";
import { 
  useGetOrCreateTrip,
  useCreateBooking, 
  useCreateBookingParticipants, 
  type CreateBookingData,
  type BookingParticipantData 
} from "@/hooks/useBookingMutations";
import { trackBookingStart } from "@/lib/activity-tracker";
import { cn } from "@/lib/utils";
import { BOOKING_STATUS } from "@/lib/booking-status";
import { TRIP_STATUS } from "@/lib/trip-status";
import type { BookingParticipantForm, Product, TripWithBookingInfo } from "@/types";

// State management types
interface BookingFormState {
  selectedDate: string;
  selectedPeople: number;
  selectedSeats: number[];
  participants: BookingParticipantForm[];
}

interface TripSelectionState {
  trip: TripWithBookingInfo | null;
  selectedTrip: TripWithBookingInfo | null;
  isCustomDateMode: boolean;
  customDate: Date | undefined;
  newTripId: number | null;
}

interface UIState {
  summaryOpen: boolean;
  seatZoom: number;
  validationErrors: { [key: string]: string };
}

type BookingFormAction =
  | { type: 'SET_DATE'; payload: string }
  | { type: 'SET_PEOPLE'; payload: number }
  | { type: 'SET_SEATS'; payload: number[] }
  | { type: 'SET_PARTICIPANTS'; payload: BookingParticipantForm[] }
  | { type: 'UPDATE_PARTICIPANT'; payload: { index: number; participant: BookingParticipantForm } }
  | { type: 'RESET_FORM' };

type TripSelectionAction =
  | { type: 'SET_TRIP'; payload: TripWithBookingInfo | null }
  | { type: 'SET_SELECTED_TRIP'; payload: TripWithBookingInfo | null }
  | { type: 'SET_CUSTOM_DATE_MODE'; payload: boolean }
  | { type: 'SET_CUSTOM_DATE'; payload: Date | undefined }
  | { type: 'SET_NEW_TRIP_ID'; payload: number | null }
  | { type: 'RESET_TRIP_SELECTION' };

type UIAction =
  | { type: 'TOGGLE_SUMMARY' }
  | { type: 'SET_SUMMARY_OPEN'; payload: boolean }
  | { type: 'SET_SEAT_ZOOM'; payload: number }
  | { type: 'SET_VALIDATION_ERRORS'; payload: { [key: string]: string } }
  | { type: 'CLEAR_VALIDATION_ERRORS' };

// Reducers
function bookingFormReducer(state: BookingFormState, action: BookingFormAction): BookingFormState {
  switch (action.type) {
    case 'SET_DATE':
      return { ...state, selectedDate: action.payload };
    case 'SET_PEOPLE':
      return { ...state, selectedPeople: action.payload };
    case 'SET_SEATS':
      return { ...state, selectedSeats: action.payload };
    case 'SET_PARTICIPANTS':
      return { ...state, participants: action.payload };
    case 'UPDATE_PARTICIPANT':
      return {
        ...state,
        participants: state.participants.map((p, i) => 
          i === action.payload.index ? action.payload.participant : p
        )
      };
    case 'RESET_FORM':
      return {
        selectedDate: '',
        selectedPeople: 1,
        selectedSeats: [],
        participants: [{ name: '', phone: '', email: '', seat: null, gender: null }]
      };
    default:
      return state;
  }
}

function tripSelectionReducer(state: TripSelectionState, action: TripSelectionAction): TripSelectionState {
  switch (action.type) {
    case 'SET_TRIP':
      return { ...state, trip: action.payload };
    case 'SET_SELECTED_TRIP':
      return { ...state, selectedTrip: action.payload };
    case 'SET_CUSTOM_DATE_MODE':
      return { ...state, isCustomDateMode: action.payload };
    case 'SET_CUSTOM_DATE':
      return { ...state, customDate: action.payload };
    case 'SET_NEW_TRIP_ID':
      return { ...state, newTripId: action.payload };
    case 'RESET_TRIP_SELECTION':
      return {
        trip: null,
        selectedTrip: null,
        isCustomDateMode: false,
        customDate: undefined,
        newTripId: null
      };
    default:
      return state;
  }
}

function uiReducer(state: UIState, action: UIAction): UIState {
  switch (action.type) {
    case 'TOGGLE_SUMMARY':
      return { ...state, summaryOpen: !state.summaryOpen };
    case 'SET_SUMMARY_OPEN':
      return { ...state, summaryOpen: action.payload };
    case 'SET_SEAT_ZOOM':
      return { ...state, seatZoom: action.payload };
    case 'SET_VALIDATION_ERRORS':
      return { ...state, validationErrors: action.payload };
    case 'CLEAR_VALIDATION_ERRORS':
      return { ...state, validationErrors: {} };
    default:
      return state;
  }
}

const Booking = () => {
  const {
    productId
  } = useParams();
  const navigate = useNavigate();
  const {
    user,
    profile,
    loading: authLoading,
    isAuthenticated
  } = useAuth();
  
  // Consolidated state with reducers
  const [bookingForm, bookingFormDispatch] = useReducer(bookingFormReducer, {
    selectedDate: '',
    selectedPeople: 1,
    selectedSeats: [],
    participants: [{ name: '', phone: '', email: '', seat: null, gender: null }]
  });
  
  const [tripSelection, tripSelectionDispatch] = useReducer(tripSelectionReducer, {
    trip: null,
    selectedTrip: null,
    isCustomDateMode: false,
    customDate: undefined,
    newTripId: null
  });
  
  const [uiState, uiDispatch] = useReducer(uiReducer, {
    summaryOpen: false,
    seatZoom: 1,
    validationErrors: {}
  });
  
  // Destructure for easier access
  const { selectedDate, selectedPeople, selectedSeats, participants } = bookingForm;
  const { trip, selectedTrip, isCustomDateMode, customDate, newTripId } = tripSelection;
  const { summaryOpen, seatZoom, validationErrors } = uiState;
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const seatContainerRef = useRef<HTMLDivElement>(null);
  const touchStartDistance = useRef<number | null>(null);

  // React Query 기반 데이터 페칭
  const {
    product,
    availableTrips: rawAvailableTrips,
    bookedSeats,
    bookedSeatsWithGender,
    isLoading,
    error,
    refetchBookedSeats
  } = useBookingData(productId, user?.id, selectedTrip?.trip_id);

  // 오늘 날짜 이후의 trips만 필터링 (프론트엔드 추가 보장)
  const availableTrips = rawAvailableTrips.filter(trip => {
    const tripDate = new Date(trip.departure_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    tripDate.setHours(0, 0, 0, 0);
    return tripDate > today; // 오늘보다 이후 날짜만 허용
  });

  // React Query mutation hooks
  const getOrCreateTripMutation = useGetOrCreateTrip();
  const createBookingMutation = useCreateBooking();
  const createParticipantsMutation = useCreateBookingParticipants();

  // 뒤로가기 핸들러 - 원래 상품 상세 페이지로 이동
  const handleGoBack = () => {
    if (product?.product_courses?.[0]?.course_id) {
      // product 정보에서 course_id를 가져와서 올바른 product-detail 경로로 이동
      navigate(`/product-detail/${product.product_courses[0].course_id}`);
    } else {
      // course_id를 찾을 수 없으면 courses 페이지로 이동
      navigate('/courses');
    }
  };
  // 예약 시작 추적 (product 로드 시)
  useEffect(() => {
    if (product) {
      trackBookingStart(product.product_id, 0, product.name, []);
    }
  }, [product]);

  // React Query가 refetchOnWindowFocus로 자동 처리
  // 브라우저 활성화 시 자동으로 데이터 동기화됨

  // 기존 예약은 예약 내역 페이지에서 처리하므로 여기서는 복원하지 않음
  // 신규 예약 페이지는 항상 깨끗한 상태로 시작
  // 자동 날짜 선택 제거 - 사용자가 명시적으로 날짜를 선택하도록 함
  // 신규 예약 페이지에서는 사용자의 명확한 의도 확인이 중요
  
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      navigate("/");
    }
  }, [isAuthenticated, authLoading, navigate]);

  // 참가 인원 수가 변경될 때 participants 배열 업데이트
  useEffect(() => {
    bookingFormDispatch({ 
      type: 'SET_PARTICIPANTS', 
      payload: (() => {
        const prevParticipants = participants;
      const newParticipants = Array.from({
        length: selectedPeople
      }, (_, index) => {
        if (index === 0 && user && profile) {
          // 첫 번째 참가자는 로그인한 사용자 정보로 설정 (기존 정보가 있으면 유지)
          return prevParticipants[0] ? {
            ...prevParticipants[0],
            name: prevParticipants[0].name || profile.name || "",
            phone: prevParticipants[0].phone || profile.phone || "",
            email: prevParticipants[0].email || user.email || "",
            gender: prevParticipants[0].gender || profile.gender || null
          } : {
            name: profile.name || "",
            phone: profile.phone || "",
            email: user.email || "",
            seat: null,
            gender: profile.gender || null
          };
        }
        // 기존 참가자 정보가 있으면 유지, 없으면 빈 정보
        return prevParticipants[index] || {
          name: "",
          phone: "",
          email: "",
          seat: null,
          gender: null
        };
      });
        return newParticipants;
      })()
    });

    // 선택된 좌석이 참가자 수보다 많으면 조정
    if (selectedSeats.length > selectedPeople) {
      bookingFormDispatch({ type: 'SET_SEATS', payload: selectedSeats.slice(0, selectedPeople) });
    }
  }, [selectedPeople, user, profile, selectedSeats]);

  const updateParticipant = (index: number, field: string, value: string) => {
    const newParticipants = [...participants];
    newParticipants[index] = {
      ...newParticipants[index],
      [field]: value
    };
    bookingFormDispatch({ type: 'SET_PARTICIPANTS', payload: newParticipants });
  };
  const validateParticipant = (index: number, field: string, value: string) => {
    const newErrors = {
      ...validationErrors
    };
    const errorKey = `participant-${index}-${field}`;
    if (field === "name") {
      if (!value.trim()) {
        newErrors[errorKey] = "이름을 입력해주세요.";
      } else if (value.trim().length < 2) {
        newErrors[errorKey] = "이름은 2글자 이상 입력해주세요.";
      } else {
        delete newErrors[errorKey];
      }
    } else if (field === "phone") {
      const phoneRegex = /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/;
      if (!value.trim()) {
        newErrors[errorKey] = "연락처를 입력해주세요.";
      } else if (!phoneRegex.test(value.replace(/-/g, ""))) {
        newErrors[errorKey] = "올바른 연락처 형식을 입력해주세요. (예: 010-1234-5678)";
      } else {
        delete newErrors[errorKey];
      }
    }
    uiDispatch({ type: 'SET_VALIDATION_ERRORS', payload: newErrors });
  };

  // 좌석 선택 함수
  const handleSeatSelect = (seatNumber: number) => {
    // 3번 좌석(인솔자 전용)은 선택할 수 없음
    if (seatNumber === 3) {
      return;
    }

    // 이미 예약된 좌석은 선택할 수 없음
    if (bookedSeats.includes(seatNumber)) {
      return;
    }
    if (selectedSeats.includes(seatNumber)) {
      // 이미 선택된 좌석이면 제거
      const newSelectedSeats = selectedSeats.filter(seat => seat !== seatNumber);
      bookingFormDispatch({ type: 'SET_SEATS', payload: newSelectedSeats });

      // 해당 좌석을 가진 참가자의 좌석 정보 제거
      const newParticipants = participants.map(participant => participant.seat === seatNumber ? {
        ...participant,
        seat: null
      } : participant);
      bookingFormDispatch({ type: 'SET_PARTICIPANTS', payload: newParticipants });
    } else {
      // 좌석을 선택하는 경우
      if (selectedSeats.length < selectedPeople) {
        // 선택 가능한 좌석 수보다 적으면 그냥 추가
        const newSelectedSeats = [...selectedSeats, seatNumber];
        bookingFormDispatch({ type: 'SET_SEATS', payload: newSelectedSeats });

        // 좌석이 배정되지 않은 첫 번째 참가자에게 좌석 배정
        const newParticipants = [...participants];
        const unassignedIndex = newParticipants.findIndex(p => p.seat === null);
        if (unassignedIndex !== -1) {
          newParticipants[unassignedIndex] = {
            ...newParticipants[unassignedIndex],
            seat: seatNumber
          };
          bookingFormDispatch({ type: 'SET_PARTICIPANTS', payload: newParticipants });
        }
      } else {
        // 이미 필요한 만큼 선택되어 있으면, 가장 먼저 선택된 좌석을 해제하고 새 좌석으로 교체
        const oldestSelectedSeat = selectedSeats[0];
        const newSelectedSeats = [...selectedSeats.slice(1), seatNumber];
        bookingFormDispatch({ type: 'SET_SEATS', payload: newSelectedSeats });

        // 가장 먼저 선택된 좌석을 가진 참가자의 좌석을 새 좌석으로 변경
        const newParticipants = participants.map(participant => participant.seat === oldestSelectedSeat ? {
          ...participant,
          seat: seatNumber
        } : participant);
        bookingFormDispatch({ type: 'SET_PARTICIPANTS', payload: newParticipants });
      }
    }
  };

  // 좌석 컴포넌트 렌더링 함수
  const renderSeat = (seatNumber: number, isGuideSeat = false) => {
    // 3번 좌석은 인솔자 전용으로 항상 확정 상태
    if (isGuideSeat) {
      return <div key={seatNumber} className="relative w-20 h-20 lg:w-16 lg:h-16 flex items-center justify-center text-body-large lg:text-body-medium font-bold rounded-lg bg-blue-500 text-white cursor-not-allowed shadow-lg">
          <div className="absolute inset-0 rounded-lg bg-blue-500">
            <div className="absolute bottom-1 left-1 right-1 h-3 rounded-b bg-blue-600"></div>
            <div className="absolute bottom-3 left-0 w-1.5 h-8 lg:h-7 rounded-l bg-blue-600"></div>
            <div className="absolute bottom-3 right-0 w-1.5 h-8 lg:h-7 rounded-r bg-blue-600"></div>
          </div>
          <span className="relative z-10 font-semibold">
            {seatNumber.toString().padStart(2, "0")}
          </span>
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-700 rounded-full flex items-center justify-center shadow">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        </div>;
    }
    const isSelected = selectedSeats.includes(seatNumber);
    const isBooked = bookedSeats.includes(seatNumber);

    // 성별 결정: 선택된 좌석이면 participants에서, 예약된 좌석이면 bookedSeatsWithGender에서
    let gender: 'male' | 'female' | null = null;
    if (isSelected) {
      const participant = participants.find(p => p.seat === seatNumber);
      gender = participant?.gender || null;
    } else if (isBooked) {
      const seatInfo = bookedSeatsWithGender.find(s => s.seat === seatNumber);
      gender = seatInfo?.gender || null;
    }

    return <div key={seatNumber} onClick={() => !isBooked && handleSeatSelect(seatNumber)} className={cn("relative w-20 h-20 lg:w-16 lg:h-16 flex flex-col items-center justify-center text-body-large lg:text-body-medium font-bold transition-all rounded-lg shadow-sm", isBooked ? "bg-gray-400 text-white cursor-not-allowed" : isSelected ? "bg-[#F39800] text-white cursor-pointer shadow-lg scale-105" : "bg-white text-gray-700 border-2 border-gray-300 hover:border-[#F39800] hover:shadow-md cursor-pointer")}>
        <div className={cn("absolute inset-0 rounded-lg", isBooked ? "bg-gray-400" : isSelected ? "bg-[#F39800]" : "bg-white border-2 border-gray-300")}>
          {/* 좌석 등받이 - 아래쪽으로 이동 */}
          <div className={cn("absolute bottom-1 left-1 right-1 h-3 rounded-b", isBooked ? "bg-gray-500" : isSelected ? "bg-[#E08700]" : "bg-gray-100")}></div>
          {/* 좌석 팔걸이 - 아래쪽으로 이동 */}
          <div className={cn("absolute bottom-3 left-0 w-1.5 h-8 lg:h-7 rounded-l", isBooked ? "bg-gray-500" : isSelected ? "bg-[#E08700]" : "bg-gray-200")}></div>
          <div className={cn("absolute bottom-3 right-0 w-1.5 h-8 lg:h-7 rounded-r", isBooked ? "bg-gray-500" : isSelected ? "bg-[#E08700]" : "bg-gray-200")}></div>
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center gap-1">
          <span className="font-semibold">
            {seatNumber.toString().padStart(2, "0")}
          </span>
          {gender && <span className={cn(
            "text-[10px] font-bold px-1.5 py-0.5 rounded",
            gender === 'male' ? "bg-blue-500 text-white" : "bg-pink-500 text-white"
          )}>
            {gender === 'male' ? '남' : '여'}
          </span>}
        </div>
        {isSelected && <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>}
      </div>;
  };

  // 좌석 배치 렌더링 함수 - 2+1 구조 (총 28석)
  const renderSeatLayout = () => {
    // 4x9 그리드 생성
    const grid = [];
    let seatNumber = 1;

    // 1-8행: 2+1 구조
    for (let row = 0; row < 8; row++) {
      const rowSeats = [];

      // 왼쪽 2석
      rowSeats.push(seatNumber++);
      rowSeats.push(seatNumber++);

      // 빈 셀 (통로)
      rowSeats.push(null);

      // 오른쪽 1석
      rowSeats.push(seatNumber++);
      grid.push(rowSeats);
    }

    // 9행: 4석 연속
    const lastRow = [];
    for (let i = 0; i < 4; i++) {
      lastRow.push(seatNumber++);
    }
    grid.push(lastRow);

    // 그리드를 렌더링
    return <div className="grid grid-cols-4 gap-3 lg:gap-3 max-w-full">
        {grid.map((row, rowIndex) => row.map((seat, colIndex) => <div key={`${rowIndex}-${colIndex}`}>
              {seat === null ?
        // 통로
        <div className="w-20 h-20 lg:w-16 lg:h-16 flex items-center justify-center">
                  {rowIndex < 8 && <div className="w-0.5 h-12 lg:h-10 bg-gray-200 rounded-full opacity-50"></div>}
                </div> :
        // 좌석
        renderSeat(seat, seat === 3)}
            </div>))}
      </div>;
  };

  // 모든 참가자 정보가 유효한지 확인 (좌석 포함)
  const isValidParticipants = () => {
    return participants.every(participant =>
      participant.name.trim() !== "" &&
      participant.phone.trim() !== "" &&
      participant.seat !== null &&
      participant.gender !== null
    );
  };

  // 좌석이 선택되지 않은 참가자가 있는지 확인
  const hasUnassignedSeats = useCallback(() => {
    return participants.some(participant => participant.seat === null);
  }, [participants]);
  
  const calculateTotalPrice = useCallback(() => {
    if (!product) return 0;
    return Number(product.price) * selectedPeople;
  }, [product, selectedPeople]);
  const handleDateSelect = (selectedDate: string) => {
    const selectedTrip = availableTrips.find(trip => trip.departure_date.split("T")[0] === selectedDate);
    if (selectedTrip) {
      // 이미 예약한 날짜나 마감된 날짜는 선택할 수 없음
      const maxParticipants = selectedTrip.max_participants;
      const isFull = maxParticipants ? selectedTrip.totalBooked >= maxParticipants : false;
      if (selectedTrip.isAlreadyBooked || isFull) {
        return;
      }
      tripSelectionDispatch({ type: 'SET_SELECTED_TRIP', payload: selectedTrip });
      tripSelectionDispatch({ type: 'SET_TRIP', payload: selectedTrip });
      bookingFormDispatch({ type: 'SET_DATE', payload: selectedDate });
      tripSelectionDispatch({ type: 'SET_CUSTOM_DATE_MODE', payload: false });
      tripSelectionDispatch({ type: 'SET_CUSTOM_DATE', payload: undefined });
      tripSelectionDispatch({ type: 'SET_NEW_TRIP_ID', payload: null });

      // 좌석 선택 상태 초기화
      bookingFormDispatch({ type: 'SET_SEATS', payload: [] });
      // 참가자의 좌석 정보만 초기화 (이름, 연락처는 유지)
      const resetParticipants = participants.map(participant => ({
        ...participant,
        seat: null
      }));
      bookingFormDispatch({ type: 'SET_PARTICIPANTS', payload: resetParticipants });

      // React Query가 자동으로 좌석 정보를 가져옴
    }
  };
  const handleCustomDateSelect = (date: Date | undefined) => {
    if (date) {
      tripSelectionDispatch({ type: 'SET_CUSTOM_DATE', payload: date });
      bookingFormDispatch({ type: 'SET_DATE', payload: format(date, "yyyy-MM-dd") });
      tripSelectionDispatch({ type: 'SET_CUSTOM_DATE_MODE', payload: true });
      tripSelectionDispatch({ type: 'SET_SELECTED_TRIP', payload: null });
      tripSelectionDispatch({ type: 'SET_TRIP', payload: null });
      tripSelectionDispatch({ type: 'SET_NEW_TRIP_ID', payload: null });

      // 좌석 선택 상태 초기화
      bookingFormDispatch({ type: 'SET_SEATS', payload: [] });
      // 참가자의 좌석 정보만 초기화 (이름, 연락처는 유지)
      const resetParticipants = participants.map(participant => ({
        ...participant,
        seat: null
      }));
      bookingFormDispatch({ type: 'SET_PARTICIPANTS', payload: resetParticipants });

      // 커스텀 날짜는 새로운 trip이므로 예약된 좌석이 없음 (React Query가 자동 처리)
    }
  };
  const handleBookingSubmit = useCallback(async () => {
;
;
    
    if (!selectedDate || selectedPeople < 1) {
      toast({
        title: "필수 정보 누락",
        description: "날짜와 인원을 선택해주세요.",
        variant: "destructive"
      });
      return;
    }

    // 참가자 정보 검증
    const hasEmptyInfo = participants.some(p => p.name.trim() === "" || p.phone.trim() === "");
    if (hasEmptyInfo) {
      toast({
        title: "참가자 정보 필수",
        description: "모든 참가자의 이름과 연락처를 입력해주세요.",
        variant: "destructive"
      });
      return;
    }

    // 좌석 선택 검증
    if (hasUnassignedSeats()) {
      toast({
        title: "좌석 선택 필수",
        description: "모든 참가자의 좌석을 선택해주세요.",
        variant: "destructive"
      });
      return;
    }
    
    if (!user?.id) {
      toast({
        title: "인증 오류",
        description: "로그인이 필요합니다.",
        variant: "destructive"
      });
      return;
    }

    try {
      // 1. tripId 초기 설정
      let tripId = selectedTrip?.trip_id || newTripId;
      const isCustomDateMode = !selectedTrip && selectedDate;
      
;

      // 2. 커스텀 날짜이고 tripId가 없는 경우에만 trip 생성/조회
      if (isCustomDateMode && !tripId) {
;
        
        const tripData = {
          product_id: parseInt(productId!),
          departure_date: selectedDate,
          created_by: user.id
        };
        
        // 동시성 안전한 함수 사용
        const tripResult = await getOrCreateTripMutation.mutateAsync(tripData);
        
        if (!tripResult?.trip_id) {
          console.error("[Booking] Trip 생성/조회 실패");
          throw new Error('Trip 생성 또는 조회에 실패했습니다.');
        }
        
        tripId = tripResult.trip_id;
;
        
        // 상태 업데이트
        tripSelectionDispatch({ 
          type: 'SET_NEW_TRIP_ID', 
          payload: tripId 
        });
      }
      
      // 3. 최종 tripId 검증
      if (!tripId) {
;
        toast({
          title: "여행 일정 선택 필수",
          description: "여행 일정을 선택해주세요.",
          variant: "destructive"
        });
        return;
      }
      
;

      // 예약 제출 시 활동 추적 (trip 정보와 좌석 정보 포함)
      if (product) {
        trackBookingStart(product.product_id, tripId, product.name, selectedSeats);
      }

      // 새 예약 생성
      const bookingData: CreateBookingData = {
        trip_id: tripId,
        num_participants: selectedPeople,
        total_price: calculateTotalPrice(),
        user_id: user.id,
        booking_date: new Date(selectedDate).toISOString(),
        status: BOOKING_STATUS.PENDING
      };
      
      const bookingResult = await createBookingMutation.mutateAsync(bookingData);
      
      // booking_id 가져오기
      const bookingId = bookingResult.booking_id;
      if (!bookingId) {
        console.error('No booking ID in response:', bookingResult);
        throw new Error('Booking creation failed - no booking ID returned');
      }

      // 참가자 정보 저장
      const participantsData: BookingParticipantData[] = participants.map((participant, index) => ({
        booking_id: bookingId,
        participant_order: index + 1,
        name: participant.name,
        phone: participant.phone,
        email: participant.email || null,
        seat: participant.seat,
        gender: participant.gender
      }));
      
      await createParticipantsMutation.mutateAsync(participantsData);
      
      // 예약 성공 - 결제 페이지로 이동
;
      navigate(`/payment/${bookingId}`);
    } catch (error: unknown) {
      console.error("Error:", error);
      
      // 좌석 중복 에러를 친화적으로 처리 (우선 순위 높음)
      if (error?.code === 'SEAT_ALREADY_TAKEN' || 
          (isPostgresError(error) && error.message?.includes('already booked'))) {
        // 좌석 정보 새로고침
        await refetchBookedSeats();
        
        // 친화적인 메시지 표시 (에러가 아닌 안내로 표시)
        toast({
          title: "다른 좌석을 선택해 주세요 😊",
          description: error.message || "방금 다른 분이 먼저 선택하신 좌석이에요. 다른 좋은 좌석을 선택해 주세요!",
          variant: "default", // destructive 대신 default 사용
          duration: 5000,
        });
        
        // 좌석 선택 초기화 (사용자가 다시 선택하도록)
        bookingFormDispatch({ type: 'SET_SEATS', payload: [] });
        const resetParticipants = participants.map(participant => ({
          ...participant,
          seat: null
        }));
        bookingFormDispatch({ type: 'SET_PARTICIPANTS', payload: resetParticipants });
        
        // 좌석 선택 섹션으로 스크롤
        const seatSection = document.querySelector('[data-section="seat-selection"]');
        if (seatSection) {
          seatSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        return; // 에러 처리 완료, 페이지 이동 없음
      }
      
      // 인솔자 좌석 예약 시도 에러
      if (error?.code === 'RESERVED_SEAT') {
        toast({
          title: "인솔자 전용 좌석입니다 🚌",
          description: error.message || "3번 좌석은 인솔자님을 위한 자리입니다.",
          variant: "default",
        });
        return;
      }
      
      // 중복 예약 에러를 친화적으로 처리
      if (isPostgresError(error) && error.code === '23505' && error.message?.includes('unique_user_trip_booking')) {
        toast({
          title: "이미 예약하신 여행입니다",
          description: "같은 여행에 중복 예약은 불가능합니다. 예약 내역을 확인해 주세요.",
          variant: "destructive"
        });
        // 예약 내역 페이지로 이동하도록 안내
        setTimeout(() => {
          navigate('/booking-history');
        }, 2000);
      } else if (error?.code === 'DUPLICATE_BOOKING') {
        toast({
          title: "이미 예약하신 여행입니다",
          description: error.message || "예약 내역을 확인해 주세요.",
          variant: "destructive"
        });
        setTimeout(() => {
          navigate('/booking-history');
        }, 2000);
      } else {
        toast({
          title: "예약 처리 오류",
          description: "예약 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
          variant: "destructive"
        });
      }
    }
  }, [
    selectedDate, selectedPeople, participants, selectedSeats, user, 
    selectedTrip, newTripId, product, productId,
    getOrCreateTripMutation, createBookingMutation, createParticipantsMutation,
    hasUnassignedSeats, calculateTotalPrice, toast, navigate
  ]);
  // 로딩 중일 때 표시
  if (isLoading || authLoading) {
    return <div className="min-h-screen bg-gray-50">
        {/* 고정 헤더 */}
        <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="container mx-auto px-4 py-3 flex items-center">
            <BackButton onClick={handleGoBack} />
            <h1 className="flex-1 text-center text-body-large font-semibold text-gray-900 pr-10">
              예약하기
            </h1>
          </div>
        </div>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#F39800]" />
          <span className="ml-2 text-body-medium text-gray-600">
            상품 정보를 불러오는 중...
          </span>
        </div>
        <Footer />
      </div>;
  }
  if (error) {
    return <div className="min-h-screen bg-gray-50">
        {/* 고정 헤더 */}
        <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="container mx-auto px-4 py-3 flex items-center">
            <BackButton onClick={handleGoBack} />
            <h1 className="flex-1 text-center text-body-large font-semibold text-gray-900 pr-10">
              예약하기
            </h1>
          </div>
        </div>
        <div className="text-center py-20">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate("/")} className="bg-[#F39800] hover:bg-[#E08700]">
            홈으로 돌아가기
          </Button>
        </div>
        <Footer />
      </div>;
  }
  if (!product) {
    return <div className="min-h-screen bg-gray-50">
        {/* 고정 헤더 */}
        <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="container mx-auto px-4 py-3 flex items-center">
            <BackButton onClick={handleGoBack} />
            <h1 className="flex-1 text-center text-body-large font-semibold text-gray-900 pr-10">
              예약하기
            </h1>
          </div>
        </div>
        <div className="text-center py-20">
          <p className="text-gray-600 text-body-large">상품 정보를 찾을 수 없습니다.</p>
          <Button onClick={() => navigate("/")} className="mt-4 bg-[#F39800] hover:bg-[#E08700]">
            홈으로 돌아가기
          </Button>
        </div>
        <Footer />
      </div>;
  }
  return <div className="min-h-screen bg-gray-50">
      {/* 고정 헤더 */}
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center">
          <BackButton onClick={handleGoBack} />
          <h1 className="flex-1 text-center text-body-large font-semibold text-gray-900 pr-10">
            예약하기
          </h1>
        </div>
      </div>

      <div className="bg-white border-b">
        
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-[#F39800] text-white rounded-full flex items-center justify-center text-body-small font-semibold">
                1
              </div>
              <span className="ml-2 text-[#F39800] font-semibold">예약하기</span>
            </div>
            <div className="w-12 h-1 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 text-gray-500 rounded-full flex items-center justify-center text-body-small">
                2
              </div>
              <span className="ml-2 text-gray-500">결제하기</span>
            </div>
            <div className="w-12 h-1 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 text-gray-500 rounded-full flex items-center justify-center text-body-small">
                3
              </div>
              <span className="ml-2 text-gray-500">예약완료</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            {/* 선택한 상품 */}
            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-body-medium md:text-body-large">
                  <MapPin className="w-5 h-5 text-[#F39800]" />
                  선택한 상품
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 md:gap-4">
                  <img src={product.main_photo_url || "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2000&auto=format&fit=crop"} alt={product.name} className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg bg-gray-200" onError={e => {
                  e.currentTarget.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCAzMkM0NC40IDMyIDQ4IDM1LjYgNDggNTZDNDggNzYuNCA0NC40IDgwIDI0IDgwQzMuNiA4MCAyIDc2LjQgMiA1NkMyIDM1LjYgNS42IDMyIDI0IDMyWiIgZmlsbD0iI0Q5RDlEOSIvPgo8L3N2Zz4K";
                }} />
                  <div className="flex-1">
                    <h3 className="font-semibold text-body-medium md:text-body-large">
                      {product.name}
                    </h3>
                    {(trip?.departure_point || selectedTrip?.departure_point) && <p className="text-gray-600 text-body-small">
                        {trip?.departure_point || selectedTrip?.departure_point}
                      </p>}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary" className="bg-[#F39800]/10 text-[#F39800] text-caption">
                        트레킹
                      </Badge>
                      <span className="text-body-small md:text-body-large font-bold text-[#F39800]">
                        {Number(product.price).toLocaleString()}원/인
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 여행 날짜 선택 */}
            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-body-medium md:text-body-large">
                  <Calendar className="w-5 h-5 text-[#F39800]" />
                  여행 날짜
                  {selectedDate && <Badge className="ml-auto bg-green-500 text-white">
                      선택됨
                    </Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {availableTrips.map(trip => {
                    const isSelected = selectedDate === trip.departure_date.split("T")[0] && !isCustomDateMode;
                    const maxParticipants = trip.max_participants;
                    const isFull = maxParticipants ? trip.totalBooked >= maxParticipants : false;
                    const isAlreadyBooked = trip.isAlreadyBooked;
                    const isClickable = !isFull && !isAlreadyBooked;
                    return <div key={trip.trip_id} onClick={() => isClickable && handleDateSelect(trip.departure_date.split("T")[0])} className={cn("p-4 border-2 rounded-lg transition-all relative", isSelected ? "border-[#F39800] bg-[#F39800]/5" : isAlreadyBooked ? "border-blue-300 bg-blue-100 cursor-not-allowed opacity-90 select-none" : isFull ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60" : "border-gray-200 hover:border-[#F39800]/50 hover:bg-[#F39800]/5 cursor-pointer")}>
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-semibold text-gray-900">
                                {format(new Date(trip.departure_date), "M월 d일 (E)", {
                              locale: ko
                            })}
                              </div>
                              {trip.departure_point && <div className="text-body-small text-gray-600 mt-1">
                                  {trip.departure_point}
                                </div>}
                            </div>
                            <div className="text-right">
                              <div className={cn("text-body-small font-medium", isFull ? "text-red-600" : isAlreadyBooked ? "text-blue-600" : "text-gray-600")}>
                                {trip.totalBooked}명 예약 중
                              </div>
                              {isFull && <Badge variant="destructive" className="text-caption mt-1">
                                  마감
                                </Badge>}
                              {isAlreadyBooked && <Badge className="text-caption mt-1 bg-blue-500 text-white">
                                  이미 예약 중
                                </Badge>}
                            </div>
                          </div>
                        </div>;
                  })}
                  </div>
                </div>

                {/* 커스텀 날짜 선택 */}
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <Label>원하는 날짜가 없나요?</Label>
                    <div className={cn(isCustomDateMode && "ring-2 ring-[#F39800] rounded-lg")}>
                      <DatePicker 
                        date={customDate} 
                        onDateChange={handleCustomDateSelect}
                        placeholder="날짜 직접 선택"
                        disabledDates={availableTrips.map(trip => new Date(trip.departure_date))}
                        maxDate={(() => {
                          const nextMonthEnd = new Date();
                          nextMonthEnd.setMonth(nextMonthEnd.getMonth() + 2);
                          nextMonthEnd.setDate(0);
                          return nextMonthEnd;
                        })()}
                        className="min-w-[180px]"
                      />
                    </div>
                  </div>
                  {isCustomDateMode && customDate && <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-body-small text-blue-800">
                        <strong>
                          {format(customDate, "M월 d일 (E)", {
                        locale: ko
                      })}
                        </strong>{" "}
                        날짜로 새로운 여행 일정이 생성됩니다.
                      </p>
                      <p className="text-caption text-blue-600 mt-1">
                        최소 참가 인원이 모이면 여행이 확정됩니다.
                      </p>
                    </div>}
                </div>
              </CardContent>
            </Card>

            {/* 인원 선택 */}
            <Card className={cn("shadow-sm transition-shadow", !selectedTrip && !selectedDate ? "opacity-50 pointer-events-none" : "hover:shadow-md")}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-body-medium md:text-body-large">
                  <Users className={cn("w-5 h-5", !selectedTrip && !selectedDate ? "text-gray-400" : "text-[#F39800]")} />
                  참가 인원
                  {!selectedTrip && !selectedDate && <Badge variant="secondary" className="ml-2 text-caption bg-gray-100 text-gray-500">
                      날짜 선택 필요
                    </Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  
                  <Select value={selectedPeople.toString()} onValueChange={value => bookingFormDispatch({ type: 'SET_PEOPLE', payload: parseInt(value) })}>
                    <SelectTrigger>
                      <SelectValue placeholder="인원을 선택하세요" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                      // 선택된 trip의 잔여 좌석 계산
                      const maxSelectableParticipants = selectedTrip && selectedTrip.max_participants ? Math.min(6, selectedTrip.max_participants - selectedTrip.totalBooked) : 6;

                      // 잔여 좌석이 없는 경우 처리
                      if (maxSelectableParticipants <= 0) {
                        return <SelectItem value="0" disabled>
                              예약 가능한 좌석이 없습니다
                            </SelectItem>;
                      }
                      return Array.from({
                        length: maxSelectableParticipants
                      }, (_, i) => i + 1).map(num => <SelectItem key={num} value={num.toString()}>
                            {num}명
                          </SelectItem>);
                    })()}
                    </SelectContent>
                  </Select>
                  {selectedTrip && selectedTrip.totalBooked > 0 && <p className="text-body-small text-gray-600 mt-2">
                      현재 {selectedTrip.totalBooked}명 예약 중
                    </p>}
                </div>
              </CardContent>
            </Card>

            {/* 참가자 정보 */}
            <Card className={cn("shadow-sm transition-shadow", !selectedTrip && !selectedDate ? "opacity-50 pointer-events-none" : "hover:shadow-md")}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-body-medium md:text-body-large">
                  참가자 정보
                  <Badge variant="destructive" className="text-caption bg-red-500 text-white">
                    필수
                  </Badge>
                  {!selectedTrip && !selectedDate ? <Badge variant="secondary" className="ml-auto text-caption bg-gray-100 text-gray-500">
                      날짜 선택 필요
                    </Badge> : isValidParticipants() && <Badge className="ml-auto bg-green-600 text-white shadow-sm">
                      <Check className="w-3 h-3 mr-1" />
                      입력 완료
                    </Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {participants.map((participant, index) => {
                const nameError = validationErrors[`participant-${index}-name`];
                const phoneError = validationErrors[`participant-${index}-phone`];
                return <div key={index} className={cn("p-3 md:p-4 border rounded-lg space-y-3 transition-all", participant.name && participant.phone ? "border-green-200 bg-green-50/50" : "border-gray-200")}>
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-[#F39800]" />
                        <h4 className="font-medium text-body-small md:text-body-medium">
                          {index === 0 ? "대표자" : `참가자 ${index + 1}`}
                        </h4>
                        {participant.seat && <Badge variant="outline" className="ml-auto text-caption">
                            좌석 {participant.seat}번
                          </Badge>}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor={`name-${index}`} className="text-body-small">
                            이름 *
                          </Label>
                          <Input id={`name-${index}`} value={participant.name} onChange={e => {
                        updateParticipant(index, "name", e.target.value);
                        validateParticipant(index, "name", e.target.value);
                      }} placeholder="이름을 입력하세요" className={cn("mt-1", nameError && "border-red-500 focus:ring-red-500")} />
                          {nameError && <p className="text-caption text-red-500 mt-1">
                              {nameError}
                            </p>}
                        </div>

                        <div>
                          <Label htmlFor={`phone-${index}`} className="text-body-small">
                            연락처 *
                          </Label>
                          <Input id={`phone-${index}`} value={participant.phone} onChange={e => {
                        updateParticipant(index, "phone", e.target.value);
                        validateParticipant(index, "phone", e.target.value);
                      }} placeholder="010-0000-0000" className={cn("mt-1", phoneError && "border-red-500 focus:ring-red-500")} />
                          {phoneError && <p className="text-caption text-red-500 mt-1">
                              {phoneError}
                            </p>}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor={`email-${index}`} className="text-body-small">
                          이메일 (선택)
                        </Label>
                        <Input id={`email-${index}`} type="email" value={participant.email} onChange={e => updateParticipant(index, "email", e.target.value)} placeholder="example@email.com" className="mt-1" />
                      </div>

                      <div className="flex items-center gap-3">
                        <Label className="text-body-small flex items-center gap-2 shrink-0">
                          성별
                          <Badge variant="destructive" className="text-caption bg-red-500 text-white">필수</Badge>
                        </Label>
                        {index === 0 && profile?.gender ? (
                          // 대표자 + 프로필에 성별이 이미 있는 경우: 프로필 성별 표시만 (변경 불가)
                          <Badge className={cn(
                            "text-body-small px-3 py-1.5",
                            profile.gender === 'male' ? 'bg-blue-500 text-white' : 'bg-pink-500 text-white'
                          )}>
                            {profile.gender === 'male' ? '♂ 남자' : '♀ 여자'}
                          </Badge>
                        ) : (
                          // 동승자 또는 대표자의 프로필 성별이 null인 경우: 선택 가능
                          <div className="flex gap-2 flex-1">
                            <Button
                              type="button"
                              variant={participant.gender === 'male' ? 'default' : 'outline'}
                              size="sm"
                              className={cn(
                                "flex-1",
                                participant.gender === 'male' ? 'bg-[#F39800] hover:bg-[#E08700]' : ''
                              )}
                              onClick={() => updateParticipant(index, "gender", participant.gender === 'male' ? null : 'male')}
                            >
                              남자
                            </Button>
                            <Button
                              type="button"
                              variant={participant.gender === 'female' ? 'default' : 'outline'}
                              size="sm"
                              className={cn(
                                "flex-1",
                                participant.gender === 'female' ? 'bg-[#F39800] hover:bg-[#E08700]' : ''
                              )}
                              onClick={() => updateParticipant(index, "gender", participant.gender === 'female' ? null : 'female')}
                            >
                              여자
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>;
              })}
              </CardContent>
            </Card>

            {/* 좌석 선택 */}
            <Card data-section="seat-selection" className={cn("shadow-sm transition-shadow", !selectedTrip && !selectedDate ? "opacity-50 pointer-events-none" : "hover:shadow-md")}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-body-medium md:text-body-large">
                  <Users className={cn("w-5 h-5", !selectedTrip && !selectedDate ? "text-gray-400" : "text-[#F39800]")} />
                  좌석 선택
                  <Badge variant="destructive" className="text-caption bg-red-500 text-white">
                    필수
                  </Badge>
                  
                  {!selectedTrip && !selectedDate ? <Badge variant="secondary" className="ml-auto text-caption bg-gray-100 text-gray-500">
                      날짜 선택 필요
                    </Badge> : selectedSeats.length === selectedPeople ? <Badge className="ml-auto bg-green-600 text-white shadow-sm">
                      <Check className="w-3 h-3 mr-1" />
                      선택 완료
                    </Badge> : <Badge variant="outline" className="ml-auto text-caption text-orange-600 border-orange-600 border-2 font-medium">
                      {selectedSeats.length}/{selectedPeople}명 선택
                    </Badge>}
                </CardTitle>
                <p className="text-body-small text-gray-600 mt-1">28인승 버스  *3번 좌석은 인솔자 전용</p>
                {hasUnassignedSeats() && selectedTrip && <Alert className="mt-3 border-2 border-red-300 bg-red-50 shadow-sm">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <AlertDescription className="text-body-small text-red-800 font-medium">
                      ⚠️ 모든 참가자의 좌석을 선택해주세요. 예약을 진행하려면 좌석 선택이 필수입니다.
                    </AlertDescription>
                  </Alert>}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 좌석 배치 - 왼쪽 정렬 */}
                  <div>
                    <div className="flex justify-start">
                      <div className="space-y-1 bg-gradient-to-b from-gray-50 to-gray-100 p-6 rounded-xl border-2 border-blue-200 shadow-lg">
                        {renderSeatLayout()}
                      </div>
                    </div>

                    {/* 범례 */}
                    <div className="mt-4">
                      <div className="grid grid-cols-4 gap-3 lg:gap-3 bg-white p-3 rounded-lg" style={{
                      width: '90%'
                    }}>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-white border border-gray-300 rounded"></div>
                          <span className="text-gray-600 text-caption">가능</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-[#F39800] border border-[#F39800] rounded"></div>
                          <span className="text-gray-600 text-caption">선택</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-400 border border-gray-400 rounded"></div>
                          <span className="text-gray-600 text-caption">이미 예약됨</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-blue-500 border border-blue-500 rounded"></div>
                          <span className="text-gray-600 text-caption">인솔자</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 우측 배정 현황 - 더 크게 */}
                  <div>
                    <div className="bg-gradient-to-b from-blue-50 to-white border-2 border-blue-100 rounded-xl p-6 shadow-lg h-full">
                      <h4 className="text-body-medium font-semibold mb-4 text-gray-700 flex items-center gap-2 border-b border-blue-100 pb-3">
                        <Users className="w-5 h-5 text-[#F39800]" />
                        좌석 배정 현황
                      </h4>


                      {/* 선택된 좌석 */}
                      {selectedSeats.length > 0 && <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="text-body-small font-medium text-green-700 mb-3">
                            선택된 좌석
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedSeats.sort((a, b) => a - b).map(seat => <Badge key={seat} className="bg-green-500 text-white text-body-small px-3 py-1">
                                  {seat}번
                                </Badge>)}
                          </div>
                        </div>}


                      {/* 예약된 좌석 */}
                      {bookedSeats.length > 0 && <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                          <div className="text-body-small font-medium text-red-700 mb-3">
                            예약된 좌석
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {bookedSeats.sort((a, b) => a - b).map(seat => {
                              const seatInfo = bookedSeatsWithGender.find(s => s.seat === seat);
                              const genderBadge = seatInfo?.gender
                                ? <span className={cn(
                                    "ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded",
                                    seatInfo.gender === 'male' ? "bg-blue-500 text-white" : "bg-pink-500 text-white"
                                  )}>
                                    {seatInfo.gender === 'male' ? '남' : '여'}
                                  </span>
                                : null;
                              return <Badge key={seat} variant="secondary" className="bg-red-100 text-red-700 text-body-small px-3 py-1 flex items-center gap-1">
                                  {seat}번 {genderBadge}
                                </Badge>;
                            })}
                          </div>
                        </div>}

                      {/* 참가자별 배정 */}
                      <div className="space-y-3">
                        <div className="text-body-small font-medium text-gray-700 mb-3">
                          참가자별 배정
                        </div>
                        <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
                          {participants.map((participant, index) => {
                            return <div key={index} className={cn("p-3 rounded-lg border text-body-small transition-colors", participant.seat ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200")}>
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-700">
                                  {index === 0 ? "대표자" : `참가자 ${index + 1}`}
                                </span>
                                <div className="flex items-center gap-2">
                                  {participant.gender && <Badge className={cn(
                                    "text-body-small px-2 py-1",
                                    participant.gender === 'male' ? "bg-blue-500 text-white" : "bg-pink-500 text-white"
                                  )}>
                                      {participant.gender === 'male' ? '♂ 남자' : '♀ 여자'}
                                    </Badge>}
                                  {participant.seat && <Badge className="bg-green-500 text-white text-body-small px-3 py-1">
                                      {participant.seat}번
                                    </Badge>}
                                </div>
                              </div>
                              <div className="text-gray-600 mt-2">
                                {participant.name || "이름 미입력"}
                              </div>
                              {!participant.seat && <div className="text-gray-400 text-body-small mt-2">
                                  좌석을 선택해주세요
                                </div>}
                            </div>;
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 예약 요약 */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 z-20 bg-white/95 backdrop-blur-sm border shadow-lg">
              <CardHeader>
                <CardTitle>예약 정보</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!selectedDate ? <div className="text-center py-8">
                    <div className="text-gray-400 mb-4">
                      <Calendar className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-body-small font-semibold">여행 날짜를 선택해주세요</p>
                    </div>
                    <p className="text-caption text-gray-500">
                      위에서 원하시는 출발 날짜를 선택하거나
                      <br />
                      날짜 직접 선택 기능을 이용해주세요
                    </p>
                  </div> : <>
                    <div className="flex justify-between">
                      <span>상품명</span>
                      <span className="font-semibold">{product.name}</span>
                    </div>
                    {selectedTrip && <div className="flex justify-between">
                        <span>버스 타입</span>
                        <span className="font-semibold">
                          {selectedTrip.bus_type || "28인승"}
                        </span>
                      </div>}
                    <div className="flex justify-between">
                      <span>날짜</span>
                      <span>
                        {selectedDate && format(new Date(selectedDate), "M월 d일 (E)", {
                      locale: ko
                    })}
                        {isCustomDateMode && <Badge variant="secondary" className="ml-2 text-caption">
                            신규 일정
                          </Badge>}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>인원</span>
                      <span>{selectedPeople}명</span>
                    </div>
                    <div className="flex justify-between">
                      <span>1인 가격</span>
                      <span>{Number(product.price).toLocaleString()}원</span>
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex justify-between text-body-large font-bold">
                        <span>총 결제금액</span>
                        <span className="text-[#F39800]">
                          {calculateTotalPrice().toLocaleString()}원
                        </span>
                      </div>
                    </div>
                    {selectedTrip && (() => {
                  const remainingSeats = selectedTrip.max_participants ? selectedTrip.max_participants - selectedTrip.totalBooked : 0;
                  if (remainingSeats <= 5 && remainingSeats > 0) {
                    return <div className="p-3 bg-amber-50 rounded-lg mt-4">
                              <p className="text-body-small text-amber-800 font-medium">
                                🔥 잔여 좌석 {remainingSeats}석만 남았습니다!
                              </p>
                              <p className="text-caption text-amber-600 mt-1">
                                서둘러 예약하세요.
                              </p>
                            </div>;
                  }
                  return null;
                })()}
                    <Button 
                      onClick={handleBookingSubmit} 
                      className="w-full bg-[#F39800] hover:bg-[#E08700] text-white" 
                      size="lg" 
                      disabled={
                        !isValidParticipants() ||
                        getOrCreateTripMutation.isPending ||
                        createBookingMutation.isPending ||
                        createParticipantsMutation.isPending
                      }
                    >
                      {(getOrCreateTripMutation.isPending ||
                        createBookingMutation.isPending ||
                        createParticipantsMutation.isPending) ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          예약 처리 중...
                        </>
                      ) : (
                        '예약하기'
                      )}
                    </Button>
                    
                    {/* 버튼 비활성화 이유 안내 */}
                    {!isValidParticipants() && (
                      <div className="mt-2 text-center">
                        <p className="text-body-small text-red-600 font-medium">
                          {(() => {
                            const hasEmptyName = participants.some(p => p.name.trim() === "");
                            const hasEmptyPhone = participants.some(p => p.phone.trim() === "");
                            const hasUnassignedSeat = participants.some(p => p.seat === null);
                            
                            if (hasEmptyName) return "참가자 이름을 모두 입력해주세요";
                            if (hasEmptyPhone) return "참가자 연락처를 모두 입력해주세요";
                            if (hasUnassignedSeat) return "모든 참가자의 좌석을 선택해주세요";
                            return "필수 정보를 모두 입력해주세요";
                          })()}
                        </p>
                      </div>
                    )}
                  </>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>;
};
export default Booking;